import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Save, PhoneOutgoing, Users, MoreVertical, Pause, Play, Archive, Trash2, RotateCcw, Zap, Phone, Loader2, Check } from "lucide-react";
import type { Campaign, CampaignStatus, ConversationFlow, AutomationRules, EvaluationCriterion } from "@type/agent";
import { USE_CASE_OPTIONS } from "@type/agent";
import { CampaignConfig } from "@component/agent-call-setup/campaign-config";
import { ConversationFlowEditor } from "@component/agent-call-setup/conversation-flow-editor";
import { EvaluationEngine } from "@component/agent-call-setup/evaluation-engine";
import { AutomationRulesEditor } from "@component/agent-call-setup/automation-rules";
import { ContactsManager } from "@component/agent-call-setup/contacts-manager";
import { ActivityLog } from "@component/agent-call-setup/activity-log";
import { CampaignResults } from "@component/agent-call-setup/campaign-results";
import { TestCallDialog } from "@component/agent-call-setup/test-call-dialog";
import { SaveCampaignConfirmDialog } from "@component/agent-call-setup/save-confirm-dialog";
import { CampaignWizardStepper } from "@component/agent-call-setup/campaign-wizard-stepper";
import { Button } from "@component/button";
import { Tooltip } from "@component/tooltip";
import Toast from "@component/toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@component/tabs";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@component/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@component/alert-dialog";
import { updateCampaignStatus, deleteAgentCampaign, uploadCampaignDocument, deleteCampaignDocument } from "@service/agent";
import { formatMYT } from "@util/date";
import { cn } from "@util/tailwind";

const statusChipConfig: Record<CampaignStatus, { label: string; dotColor: string; badgeClass: string }> = {
    active: { label: "Active", dotColor: "bg-green-500", badgeClass: "bg-green-50 text-green-700 border-green-200" },
    paused: { label: "Paused", dotColor: "bg-yellow-500", badgeClass: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    archived: { label: "Archived", dotColor: "bg-gray-400", badgeClass: "bg-gray-100 text-gray-600 border-gray-200" },
    draft: { label: "Draft", dotColor: "bg-blue-400", badgeClass: "bg-blue-50 text-blue-700 border-blue-200" },
    completed: { label: "Completed", dotColor: "bg-emerald-500", badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const campaignActions: Record<CampaignStatus, {
    primary: { label: string; icon: React.ComponentType<{ className?: string }>; nextStatus: CampaignStatus; style: string } | null;
    confirmTitle: string;
    confirmMsg: string;
    menu: { label: string; action: string; icon: React.ComponentType<{ className?: string }>; destructive?: boolean }[];
}> = {
    active: {
        primary: { label: "Pause Campaign", icon: Pause, nextStatus: "paused", style: "outline" },
        confirmTitle: "Pause Campaign?",
        confirmMsg: "This will stop all scheduled and in-progress calls. Contacts that haven't been reached yet will not be called until you resume.",
        menu: [
            { label: "Archive", action: "archive", icon: Archive },
            { label: "Delete", action: "delete", icon: Trash2, destructive: true },
        ],
    },
    paused: {
        primary: { label: "Resume Campaign", icon: Play, nextStatus: "active", style: "green" },
        confirmTitle: "Resume Campaign?",
        confirmMsg: "This will restart outbound calls to remaining contacts in the queue. Already-completed calls will not be repeated.",
        menu: [
            { label: "Archive", action: "archive", icon: Archive },
            { label: "Delete", action: "delete", icon: Trash2, destructive: true },
        ],
    },
    draft: {
        primary: { label: "Activate Campaign", icon: Zap, nextStatus: "active", style: "accent" },
        confirmTitle: "Activate Campaign?",
        confirmMsg: "This will start calling contacts based on your trigger settings. Make sure your conversation flow and contacts list are configured.",
        menu: [
            { label: "Delete", action: "delete", icon: Trash2, destructive: true },
        ],
    },
    archived: {
        primary: { label: "Restore Campaign", icon: RotateCcw, nextStatus: "paused", style: "outline" },
        confirmTitle: "Restore Campaign?",
        confirmMsg: "This will move the campaign back to Paused status. You can then review settings before resuming calls.",
        menu: [
            { label: "Delete", action: "delete", icon: Trash2, destructive: true },
        ],
    },
    completed: {
        primary: null,
        confirmTitle: "",
        confirmMsg: "",
        menu: [
            { label: "Archive", action: "archive", icon: Archive },
            { label: "Delete", action: "delete", icon: Trash2, destructive: true },
        ],
    },
};

const WIZARD_STEPS = [
    { key: "config", label: "Configuration" },
    { key: "flow", label: "Conversation Flow" },
    { key: "evaluation", label: "Evaluation" },
    { key: "trigger", label: "Trigger" },
    { key: "contacts", label: "Contacts" },
];

interface CampaignFormContainerProps {
    initialCampaign: Campaign;
    isNew?: boolean;
    onSave?: (campaign: Campaign) => Promise<void>;
    onWizardSave?: (campaign: Campaign) => Promise<string>;
    onWizardComplete?: (campaign: Campaign) => Promise<void>;
    onActivate?: (campaign: Campaign) => Promise<void>;
}

export default function CampaignFormContainer({ initialCampaign, isNew, onSave, onWizardSave, onWizardComplete, onActivate }: CampaignFormContainerProps) {
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState<Campaign>(initialCampaign);
    const [saving, setSaving] = useState(false);
    const [finishing, setFinishing] = useState(false);
    const [savedToast, setSavedToast] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Resync local state when the parent provides a fresh campaign (e.g. after a
    // save-triggered refetch on the edit page). The wizard's initialCampaign is a
    // stable module constant, so this never clobbers in-progress wizard input.
    useEffect(() => {
        setCampaign(initialCampaign);
    }, [initialCampaign]);
    const [showTestCall, setShowTestCall] = useState(false);
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);
    const [confirmAction, setConfirmAction] = useState<{ type: "status" | "archive" | "delete"; title: string; description: string; nextStatus?: CampaignStatus } | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Wizard state (only used when isNew)
    const [wizardStep, setWizardStep] = useState(0);
    const [wizardSaved, setWizardSaved] = useState(false);

    const handleCampaignChange = (updates: Partial<Campaign>) => {
        setCampaign((prev) => ({ ...prev, ...updates }));
    };

    const handleFlowChange = (flow: ConversationFlow) => {
        setCampaign((prev) => ({
            ...prev,
            openingScript: flow.openingScript,
            closingScript: flow.closingScript,
            coreQuestions: flow.coreQuestions,
            conditionalLogic: flow.conditionalLogic as unknown as Record<string, unknown>,
            conversationMode: flow.conversationMode,
            objective: flow.objective,
            knowledgeDocuments: flow.knowledgeDocuments,
            conversationGoal: flow.conversationGoal,
            topicAreas: flow.topicAreas,
            probingDepth: flow.probingDepth,
            guardrails: flow.guardrails,
        }));
    };

    const handleCriteriaChange = (evaluationCriteria: EvaluationCriterion[]) => {
        setCampaign((prev) => ({ ...prev, evaluationCriteria }));
    };

    const handleAutomationChange = (rules: AutomationRules) => {
        setCampaign((prev) => ({
            ...prev,
            triggerMethod: rules.triggerMethod,
            retryAttempts: rules.retryAttempts,
            retryDelayMinutes: rules.retryDelayMinutes,
            webhookUrl: rules.webhookUrl,
            apiEndpoint: rules.apiEndpoint,
            scheduleConfig: (rules.scheduleConfig || {}) as Record<string, unknown>,
        }));
    };

    const totalWeight = campaign.evaluationCriteria
        .filter((c) => c.enabled)
        .reduce((sum, c) => sum + c.weight, 0);

    const isActive = campaign.status === "active";

    const canSave =
        !saving &&
        !isActive &&
        campaign.name.trim().length > 0 &&
        campaign.useCase.trim().length > 0 &&
        campaign.maxDurationSeconds > 0 &&
        (campaign.evaluationCriteria.length === 0 || totalWeight === 100);

    // Surface the specific reason Save is disabled so the user isn't left
    // wondering. Order mirrors the `canSave` checks above.
    const saveDisabledReason = (() => {
        if (isActive) return "Campaign is active. Pause it first to edit.";
        if (campaign.name.trim().length === 0) return "Name is required.";
        if (campaign.useCase.trim().length === 0) return "Use case is required.";
        if (!(campaign.maxDurationSeconds > 0))
            return "Set a max call duration in Configuration.";
        if (campaign.evaluationCriteria.length > 0 && totalWeight !== 100)
            return `Evaluation weights must sum to 100 (currently ${totalWeight}).`;
        return undefined;
    })();



    const handleActivateClick = () => {
        setShowSaveConfirm(true);
    };

    const handleConfirmActivate = async () => {
        setShowSaveConfirm(false);
        setSaving(true);
        setError(null);
        try {
            await onActivate?.(campaign);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to activate campaign. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const conversationFlow = {
        conversationMode: campaign.conversationMode || "scripted" as const,
        objective: campaign.objective,
        knowledgeDocuments: campaign.knowledgeDocuments,
        openingScript: campaign.openingScript,
        coreQuestions: campaign.coreQuestions,
        closingScript: campaign.closingScript,
        conditionalLogic: Array.isArray(campaign.conditionalLogic) ? campaign.conditionalLogic : [],
        conversationGoal: campaign.conversationGoal,
        topicAreas: campaign.topicAreas,
        probingDepth: campaign.probingDepth,
        guardrails: campaign.guardrails,
    };

    const automationRules: AutomationRules = {
        triggerMethod: campaign.triggerMethod,
        retryAttempts: campaign.retryAttempts,
        retryDelayMinutes: campaign.retryDelayMinutes,
        webhookUrl: campaign.webhookUrl,
        apiEndpoint: campaign.apiEndpoint,
        scheduleConfig: campaign.scheduleConfig as unknown as AutomationRules["scheduleConfig"],
    };

    const handleDocumentUpload = async (file: File) => {
        const doc = await uploadCampaignDocument(campaign.id, file);
        handleCampaignChange({
            knowledgeDocuments: [...(campaign.knowledgeDocuments || []), doc],
        });
    };

    const handleDocumentRemove = async (docId: string) => {
        await deleteCampaignDocument(campaign.id, docId);
        handleCampaignChange({
            knowledgeDocuments: (campaign.knowledgeDocuments || []).filter((d) => d.id !== docId),
        });
    };

    // Determine timing mode from scheduleConfig
    const scheduleConfig = campaign.scheduleConfig as Record<string, unknown> | undefined;
    const timingMode = scheduleConfig?.timingMode as string | undefined;
    const scheduledDate = scheduleConfig?.scheduledDate as string | undefined;
    const scheduledTime = scheduleConfig?.scheduledTime as string | undefined;

    const actions = campaignActions[campaign.status];

    const handleStatusAction = async (newStatus: CampaignStatus) => {
        setActionLoading(true);
        try {
            await updateCampaignStatus(campaign.id, newStatus);
            handleCampaignChange({ status: newStatus });
        } catch {
            setError("Failed to update campaign status.");
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handleDeleteCampaign = async () => {
        setActionLoading(true);
        try {
            await deleteAgentCampaign(campaign.id);
            navigate("/agent/call-setup");
        } catch {
            setError("Failed to delete campaign.");
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    // Wizard: handle "Next" from Step 0 (Configuration) → auto-save
    const handleWizardNext = async () => {
        if (wizardStep === 0 && !wizardSaved) {
            // Auto-save the campaign
            if (!canSave) return;
            setSaving(true);
            setError(null);
            try {
                if (onWizardSave) {
                    const newId = await onWizardSave(campaign);
                    setCampaign((prev) => ({ ...prev, id: newId }));
                    setWizardSaved(true);
                } else if (onSave) {
                    await onSave(campaign);
                    return; // onSave navigates away
                }
            } catch {
                setError("Failed to create campaign. Please try again.");
                return;
            } finally {
                setSaving(false);
            }
        }
        setWizardStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1));
    };

    const handleWizardBack = () => {
        setWizardStep((s) => Math.max(s - 1, 0));
    };

    const handleWizardFinish = async () => {
        // Persist every step (flow, evaluation, trigger) before leaving the wizard.
        // The step-0 auto-save only created the campaign with the Configuration tab;
        // without this the later tabs are never sent to the backend and come back
        // empty on the campaign page. The campaign stays in Draft — activation is a
        // separate, explicit action on the campaign page.
        if (onWizardComplete && campaign.id) {
            setFinishing(true);
            setError(null);
            try {
                await onWizardComplete(campaign);
            } catch {
                setError("Failed to save campaign setup. Please try again.");
                setFinishing(false);
                return;
            }
            setFinishing(false);
        }

        if (campaign.id) {
            navigate(`/agent/call-setup/${campaign.id}`);
        } else {
            navigate("/agent/call-setup");
        }
    };

    const tabTriggerClass = "rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-gray-900 dark:data-[state=active]:text-white text-gray-400 px-5 py-3 text-sm font-medium";

    // Whether KB uploads are allowed (campaign is saved)
    const isUnsaved = isNew && !wizardSaved;

    // ──────────────────────────────────────────────
    // WIZARD RENDER (new campaigns)
    // ──────────────────────────────────────────────
    if (isNew) {
        const renderWizardStepContent = () => {
            switch (WIZARD_STEPS[wizardStep].key) {
                case "config":
                    return <CampaignConfig campaign={campaign} onChange={handleCampaignChange} disabled={false} />;
                case "flow":
                    return (
                        <ConversationFlowEditor
                            flow={conversationFlow}
                            onChange={handleFlowChange}
                            disabled={false}
                            isNew={isUnsaved}
                            onUploadDocument={handleDocumentUpload}
                            onRemoveDocument={handleDocumentRemove}
                        />
                    );
                case "evaluation":
                    return <EvaluationEngine criteria={campaign.evaluationCriteria} onChange={handleCriteriaChange} disabled={false} />;
                case "trigger":
                    return <AutomationRulesEditor rules={automationRules} onChange={handleAutomationChange} disabled={false} />;
                case "contacts":
                    return wizardSaved ? (
                        <ContactsManager
                            campaignId={campaign.id}
                            onContactCountChange={(count) => handleCampaignChange({ contactCount: count })}
                            disabled={false}
                        />
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="mb-4 rounded-full bg-blue-50 dark:bg-blue-900/30 p-4">
                                <Users className="w-8 h-8 text-blue-500" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Campaign not saved yet.</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                                Go back to Configuration and complete the setup first. The campaign will be auto-saved when you proceed.
                            </p>
                        </div>
                    );
                default:
                    return null;
            }
        };

        const isLastStep = wizardStep === WIZARD_STEPS.length - 1;
        const isFirstStep = wizardStep === 0;
        const canGoNext = isFirstStep ? canSave : true;

        return (
            <div className="space-y-6">
                {/* Back navigation */}
                <button
                    onClick={() => navigate("/agent/call-setup")}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Campaigns
                </button>

                {/* Header Card with Stepper */}
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                    <div className="px-7 py-5">
                        <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                    <Phone className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                                        New Campaign
                                    </h1>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">
                                        Set up your campaign step by step.
                                    </p>
                                </div>
                            </div>
                            {error && <p className="text-sm text-red-600">{error}</p>}
                        </div>
                    </div>

                    {/* Stepper */}
                    <div className="border-t border-gray-100 dark:border-gray-800 bg-[#fafbfd] dark:bg-gray-800/30 py-4">
                        <CampaignWizardStepper
                            steps={WIZARD_STEPS.map((s) => s.label)}
                            currentStep={wizardStep}
                            completedSteps={wizardSaved ? Math.max(wizardStep, 1) : 0}
                            onStepClick={(step) => {
                                // Allow navigating to completed steps or current step
                                if (wizardSaved || step === 0) {
                                    setWizardStep(step);
                                }
                            }}
                        />
                    </div>
                </div>

                {/* Step Content */}
                <div className="mt-6">
                    {renderWizardStepContent()}
                </div>

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between pt-2 pb-4">
                    <Button
                        variant="outline"
                        onClick={handleWizardBack}
                        disabled={isFirstStep}
                        className={isFirstStep ? "invisible" : ""}
                    >
                        <ArrowLeft className="w-4 h-4 mr-1.5" />
                        Back
                    </Button>

                    <div className="flex items-center gap-3">
                        {isLastStep ? (
                            <Button onClick={handleWizardFinish} disabled={finishing}>
                                {finishing ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-4 h-4 mr-1.5" />
                                        Finish Setup
                                    </>
                                )}
                            </Button>
                        ) : (
                            <Button onClick={handleWizardNext} disabled={!canGoNext || saving}>
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        {isFirstStep && !wizardSaved ? "Save & Continue" : "Next"}
                                        <ArrowRight className="w-4 h-4 ml-1.5" />
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ──────────────────────────────────────────────
    // EXISTING CAMPAIGN RENDER (flat tabs)
    // ──────────────────────────────────────────────
    return (
        <div className="space-y-6">
            {/* Back navigation */}
            <button
                onClick={() => navigate("/agent/call-setup")}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
            >
                <ArrowLeft className="w-4 h-4" />
                Back to Campaigns
            </button>

            <Tabs defaultValue="results">
                {/* Header Card */}
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                    {/* Top section */}
                    <div className="px-7 py-5">
                        <div className="flex items-start justify-between">
                            {/* Left: Icon + Title */}
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                    <Phone className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-3">
                                        <h1 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                                            {campaign.name}
                                        </h1>
                                        <span className={cn(
                                            "inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-md",
                                            statusChipConfig[campaign.status].badgeClass
                                        )}>
                                            {statusChipConfig[campaign.status].dotColor !== "bg-blue-400" && statusChipConfig[campaign.status].dotColor !== "bg-gray-400" && (
                                                <span className={cn("w-[5px] h-[5px] rounded-full", statusChipConfig[campaign.status].dotColor)} />
                                            )}
                                            {statusChipConfig[campaign.status].label}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">
                                        {USE_CASE_OPTIONS[campaign.useCase]?.label || campaign.useCase}
                                        <span className="mx-2 inline-block w-[3px] h-[3px] rounded-full bg-gray-300 align-middle" />
                                        Created {formatMYT(campaign.createdAt, "D MMM YYYY")}
                                        {campaign.activatedAt && campaign.status === "active" && (
                                            <>
                                                <span className="mx-2 inline-block w-[3px] h-[3px] rounded-full bg-gray-300 align-middle" />
                                                <span className="text-green-600">Live since {formatMYT(campaign.activatedAt, "D MMM YYYY, h:mm A")}</span>
                                            </>
                                        )}
                                    </p>
                                </div>
                            </div>

                            {/* Right: Action buttons */}
                            <div className="flex items-center gap-2">
                                {error && <p className="text-sm text-red-600 mr-2">{error}</p>}

                                {onSave && !isActive && (
                                    <Tooltip content={saveDisabledReason}>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={!canSave}
                                            onClick={async () => {
                                                setSaving(true);
                                                setError(null);
                                                try {
                                                    await onSave(campaign);
                                                    setSavedToast(true);
                                                } catch {
                                                    setError("Failed to save campaign.");
                                                } finally {
                                                    setSaving(false);
                                                }
                                            }}
                                        >
                                            {saving ? (
                                                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                                            ) : (
                                                <Save className="w-4 h-4 mr-1.5" />
                                            )}
                                            {saving ? "Saving..." : "Save"}
                                        </Button>
                                    </Tooltip>
                                )}

                                <Button variant="outline" size="sm" onClick={() => setShowTestCall(true)}>
                                    <PhoneOutgoing className="w-4 h-4 mr-1.5" />
                                    Test Campaign
                                </Button>

                                {/* Contextual primary action */}
                                {actions.primary && (
                                    <Button
                                        variant={actions.primary.style === "outline" ? "outline" : "default"}
                                        size="sm"
                                        className={cn(
                                            actions.primary.style === "green" && "bg-green-600 hover:bg-green-700 text-white border-0",
                                            actions.primary.style === "accent" && "bg-blue-600 hover:bg-blue-700 text-white border-0",
                                        )}
                                        disabled={actionLoading || saving}
                                        onClick={() => {
                                            if (campaign.status === "draft" || campaign.status === "paused") {
                                                handleActivateClick();
                                            } else {
                                                setConfirmAction({
                                                    type: "status",
                                                    title: actions.confirmTitle,
                                                    description: actions.confirmMsg,
                                                    nextStatus: actions.primary!.nextStatus,
                                                });
                                            }
                                        }}
                                    >
                                        <actions.primary.icon className="w-4 h-4 mr-1.5" />
                                        {saving ? "Processing..." : actions.primary.label}
                                    </Button>
                                )}

                                {/* 3-dot menu */}
                                {actions.menu.length > 0 && (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="outline" size="icon" className="h-9 w-9">
                                                <MoreVertical className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            {actions.menu.map((item) => (
                                                <DropdownMenuItem
                                                    key={item.action}
                                                    className={cn(item.destructive && "text-red-600 focus:text-red-600")}
                                                    onClick={() => {
                                                        if (item.action === "archive") {
                                                            setConfirmAction({
                                                                type: "archive",
                                                                title: "Archive Campaign?",
                                                                description: "This will move the campaign to archived status. It can be restored later.",
                                                                nextStatus: "archived",
                                                            });
                                                        } else if (item.action === "delete") {
                                                            setConfirmAction({
                                                                type: "delete",
                                                                title: "Delete Campaign?",
                                                                description: "This action is permanent and cannot be undone. All call history, recordings, and contact data associated with this campaign will be deleted.",
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <item.icon className="w-4 h-4 mr-2" />
                                                    {item.label}
                                                </DropdownMenuItem>
                                            ))}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tabs at bottom of card */}
                    <div className="border-t border-gray-100 dark:border-gray-800 bg-[#fafbfd] dark:bg-gray-800/30 px-7">
                        <TabsList className="bg-transparent h-auto p-0 space-x-0">
                            <TabsTrigger value="results" className={tabTriggerClass}>Results</TabsTrigger>
                            <TabsTrigger value="config" className={tabTriggerClass}>Configuration</TabsTrigger>
                            <TabsTrigger value="contacts" className={tabTriggerClass}>Contacts</TabsTrigger>
                            <TabsTrigger value="flow" className={tabTriggerClass}>Conversation Flow</TabsTrigger>
                            <TabsTrigger value="evaluation" className={tabTriggerClass}>Evaluation</TabsTrigger>
                            <TabsTrigger value="trigger" className={tabTriggerClass}>Trigger</TabsTrigger>
                            <TabsTrigger value="activity" className={tabTriggerClass}>Activity</TabsTrigger>
                        </TabsList>
                    </div>
                </div>

                {/* Tab content (outside the card) */}
                {!isNew && (
                    <TabsContent value="results" className="mt-6">
                        <CampaignResults
                            campaignId={campaign.id}
                            sessionCount={campaign.sessionCount}
                            contactCount={campaign.contactCount}
                            retryAttempts={campaign.retryAttempts}
                            campaignStatus={campaign.status}
                            onCampaignCompleted={() => handleCampaignChange({ status: "completed" })}
                        />
                    </TabsContent>
                )}
                <TabsContent value="config" className="mt-6">
                    <CampaignConfig campaign={campaign} onChange={handleCampaignChange} disabled={isActive} />
                </TabsContent>
                <TabsContent value="contacts" className="mt-6">
                    <ContactsManager
                        campaignId={campaign.id}
                        onContactCountChange={(count) => handleCampaignChange({ contactCount: count })}
                        disabled={isActive}
                    />
                </TabsContent>
                <TabsContent value="flow" className="mt-6">
                    <ConversationFlowEditor
                        flow={conversationFlow}
                        onChange={handleFlowChange}
                        disabled={isActive}
                        isNew={false}
                        onUploadDocument={handleDocumentUpload}
                        onRemoveDocument={handleDocumentRemove}
                    />
                </TabsContent>
                <TabsContent value="evaluation" className="mt-6">
                    <EvaluationEngine criteria={campaign.evaluationCriteria} onChange={handleCriteriaChange} disabled={isActive} />
                </TabsContent>
                <TabsContent value="trigger" className="mt-6">
                    <AutomationRulesEditor rules={automationRules} onChange={handleAutomationChange} disabled={isActive} />
                </TabsContent>
                <TabsContent value="activity" className="mt-6">
                    <ActivityLog campaignId={campaign.id} />
                </TabsContent>
            </Tabs>

            {/* Confirmation AlertDialog */}
            <AlertDialog open={!!confirmAction} onOpenChange={() => !actionLoading && setConfirmAction(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirmAction?.title}</AlertDialogTitle>
                        <AlertDialogDescription>{confirmAction?.description}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={actionLoading}
                            className={cn(confirmAction?.type === "delete" && "bg-red-600 hover:bg-red-700")}
                            onClick={() => {
                                if (confirmAction?.type === "delete") {
                                    handleDeleteCampaign();
                                } else if (confirmAction?.nextStatus) {
                                    handleStatusAction(confirmAction.nextStatus);
                                }
                            }}
                        >
                            {actionLoading ? "Processing..." : "Confirm"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Dialogs */}
            {!isNew && (
                <TestCallDialog
                    open={showTestCall}
                    onOpenChange={setShowTestCall}
                    campaignId={campaign.id}
                    onCallInitiated={(sessionId) => navigate(`/agent/calls/${sessionId}`)}
                />
            )}

            <SaveCampaignConfirmDialog
                open={showSaveConfirm}
                onOpenChange={setShowSaveConfirm}
                onConfirm={handleConfirmActivate}
                contactCount={campaign.contactCount}
                timingMode={timingMode || "immediate"}
                scheduledDate={scheduledDate}
                scheduledTime={scheduledTime}
                title={campaign.status === "paused" ? "Resume Campaign" : "Activate Campaign"}
                confirmLabel={campaign.status === "paused" ? "Confirm & Resume" : "Confirm & Activate"}
            />

            <Toast
                message="Changes saved"
                visible={savedToast}
                onClose={() => setSavedToast(false)}
            />
        </div>
    );
}
